// Die acht Kisten im CRM – was die Seitenleiste, die Übersicht und die
// Platzhalter-Seiten über sie zeigen. Name, „wer“ und Stufe kommen aus dem
// Kern (daten/kisten.json, eine Quelle); hier steht nur, was die Oberfläche
// dazu braucht: das Zeichen, wohin ein Klick führt, welches Recht diese Seite
// braucht, und für die Kisten, die noch nicht gebaut sind, der Satz, was
// dort kommt, und wann (CLAUDE.md, Grundsatz 8: „Was später kommt, ist
// schon als Platzhalter sichtbar“).
//
// Das „Wann“ folgt dem Fahrplan für Manuel vom 21.10.2026 (PLAN.md,
// Abschnitt 5): Termin, Montage, Inbetriebnahme im Dezember, Lead und
// Nachsorge ab Jänner – eine Schätzung, deshalb „geplant“.

import { KISTEN, type Kiste, type KistenStufe, type Recht } from "@happy-energy/kern";
import type { IkonName } from "@happy-energy/oberflaeche";

export interface KistenAnzeige {
  ikon: IkonName;
  /** Wohin ein Klick in der Seitenleiste führt. */
  pfad: string;
  /**
   * Das Recht, das die Seite dahinter braucht – bei den Kisten, die nur ein
   * Platzhalter sind, null: Die sieht, wer den ganzen Weg sieht (Übersicht).
   */
  recht: Recht | null;
  /** Wie der Eintrag heißt, wenn eine Rolle nur diese eine Kiste sieht (Montage: „Unterlagen“). */
  eigenerName?: string;
  /** Was die Kiste tut – ein Satz. */
  satz: string;
  /** Was dort kommt. */
  punkte: string[];
  /** Bei „teil“ und „s1“: was davon schon in Stufe 1 da ist („Schon da:“); bei „spaeter“: wie es bis dahin geht („Bis dahin:“). */
  schonDa?: string;
  /** Wann sie kommt – kurz, für den Chip. */
  wann: string;
}

const ANZEIGE: Record<string, KistenAnzeige> = {
  lead: {
    ikon: "funken",
    pfad: "/kiste/lead",
    recht: null,
    satz:
      "Täglich kommen Anfragen aus allen Kanälen herein. Das Lead-Management verteilt sie an den Verkauf – auch an Franchise-Nehmer, die die Kunden zugespielt bekommen.",
    punkte: ["Neue Anfragen mit Kontakt, sofort beim Verkäufer am Handy", "Statistik je Kanal – auch „KI-Suche“ als eigener Kanal", "Bleibt im Franchise bei Happy Energy"],
    schonDa: "Anfragen laufen wie heute; der Kunde entsteht mit der Besichtigung in der Doku-App.",
    wann: "geplant ab Jänner",
  },
  doku: {
    ikon: "kamera",
    pfad: "/pruefen",
    recht: "aufnahmen-liste",
    eigenerName: "Meine Besichtigungen",
    satz: "Die Besichtigung beim Kunden mit Happy Energy Doku – Station für Station, mit Fotos, Zeichnungen, Notizen und Längen.",
    punkte: [],
    schonDa: "Jede gesendete Besichtigung steht hier; was jetzt mit ihr zu tun ist – Freigeben, Bestellen, die Partien –, steht unter „Zu tun“.",
    wann: "Stufe 1",
  },
  // Seit E3c (S4) gebaut: alle Montagetermine mit Dach- und Bodenpartie, darunter die Anlagen ohne Termin.
  termin: {
    ikon: "kalender",
    pfad: "/kiste/termin",
    recht: "einsatz",
    satz: "Noch beim Kunden macht der Verkäufer den Montagetermin aus: Welche Partie hat in rund drei Wochen Platz?",
    punkte: ["Partien mit freien Plätzen auf einen Blick", "Termin gleich aus der Doku heraus", "Material und Unterlage hängen am Termin"],
    schonDa:
      "Montagetermin als Feld in der Akte und in der Doku-App – Tag und Text, mit dem Kunden vereinbart, ohne Kalender. In der Übersicht steht, wo er noch fehlt, mit dem Knopf „Eintragen“.",
    wann: "geplant im Dezember",
  },
  // Seit E3c (S5) gebaut: wo der K2-Plan oder die Liste (Excel) noch fehlt – je Zeile in die Akte (dasselbe Recht wie sie).
  dachplanung: {
    ikon: "dach",
    pfad: "/kiste/dachplanung",
    recht: "kunden",
    satz: "Die Dachplanung macht K2 – draußen beim Kunden. Plan (PDF) und Liste (Excel) liegen danach bei der Anlage.",
    punkte: ["Stückliste automatisch auslesen – Module und Schienen in die Materialliste", "Unterschrift der Kundin zur Dachplanung", "Plan und Liste kommen von selbst aus K2 zurück"],
    schonDa: "„Dachplanung starten“ in der HE-Doku öffnet K2; Plan und Liste legt der Verkäufer dort ab. Die Kiste zeigt, wo eines davon noch fehlt.",
    wann: "Auslesen geplant bis Ende Dezember",
  },
  // Seit E3c (H26, H27): kein großes Lager mehr – nach der Freigabe wird beim
  // Lieferanten bestellt. Seit S3 gebaut: was offen und was bestellt ist.
  bestellung: {
    ikon: "paket",
    pfad: "/kiste/bestellung",
    recht: "bestellen",
    satz: "Nach der Freigabe bestellt das Büro Unterkonstruktion, Module, Wechselrichter und Speicher beim Lieferanten – mit einem Klick, die Mail ist schon fertig.",
    punkte: ["Je Teil der Lieferant zum Antippen", "Wunschliefertermin aus dem Montagetermin", "Was offen ist und was bestellt ist, auf einen Blick"],
    schonDa: "In der Akte jeder Anlage die Karte „Bestellung“ – je Teil der Lieferant, die Mail fertig zum Öffnen; die Kiste zeigt, was offen und was bestellt ist.",
    wann: "seit November",
  },
  montage: {
    ikon: "werkzeug",
    pfad: "/unterlagen",
    recht: "unterlagen",
    eigenerName: "Unterlagen",
    satz: "Die Montage bekommt die Unterlage der freigegebenen Besichtigung. Danach kommt die eigene App der Partie.",
    punkte: ["Baustellen der Woche je Partie", "Unterlage Schritt für Schritt", "Fertig-Meldung an die Inbetriebnahme"],
    schonDa: "Die Unterlage für den Monteur je Anlage, zum Drucken.",
    wann: "eigene App geplant im Dezember",
  },
  inbetriebnahme: {
    ikon: "blitz",
    pfad: "/kiste/inbetriebnahme",
    recht: null,
    satz:
      "Die Inbetriebnahme prüft kritisch, entlässt die Monteure, schult die Kundin ein – und hakt intern ab. Was der Verkauf schon abgefragt hat, ist schon drin.",
    punkte: ["Checkliste in der eigenen App", "Prüfprotokoll von selbst", "Drohnenflug, Kunden-Mappe, Zertifikat"],
    schonDa: "Die Inbetriebnahme liest im CRM die Unterlagen für die Montage.",
    wann: "geplant im Dezember",
  },
  nachsorge: {
    ikon: "video",
    pfad: "/kiste/nachsorge",
    recht: null,
    satz: "Drei Tage nach der Inbetriebnahme bekommt die Kundin per WhatsApp ein Happy-Energy-Video: Drohnenvideo von ihrem Haus, Musik, Gruß vom Verkäufer.",
    punkte: ["Zuerst halbautomatisch mit Freigabe-Knopf", "WhatsApp Business früh beantragen – dauert Wochen", "Später auch: Folgeangebote zur passenden Zeit"],
    wann: "geplant ab Jänner",
  },
};

/** Was eine Kiste nicht kennt (eine neue in daten/kisten.json), ist ein Platzhalter ohne Zeichen. */
const UNBEKANNT = (k: Kiste): KistenAnzeige => ({
  ikon: "ebenen",
  pfad: `/kiste/${k.schluessel}`,
  recht: null,
  satz: k.wer,
  punkte: [],
  wann: "später",
});

export interface KisteGanz extends Kiste, KistenAnzeige {}

/** Die acht Kisten mit allem, was die Oberfläche braucht – in der Reihenfolge des Kerns. */
export const KISTEN_ANZEIGE: readonly KisteGanz[] = KISTEN.map((k) => ({ ...k, ...(ANZEIGE[k.schluessel] ?? UNBEKANNT(k)) }));

export function kisteFinden(schluessel: string): KisteGanz | undefined {
  return KISTEN_ANZEIGE.find((k) => k.schluessel === schluessel);
}

/**
 * Der Text zum Punkt der Stufe – für die Seitenleiste und die
 * Platzhalter-Seiten. Im Weg der Übersicht steht er seit dem 22.10.2026 nicht
 * mehr (Gernot: „das verwirrt“). Seit E3b (O73) ohne die Bau-Wörter „Stufe 1“:
 * da – teils da – kommt später.
 */
export const STUFE_TEXT: Record<KistenStufe, string> = { s1: "da", teil: "teils da", spaeter: "kommt später" };
